import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { haptic } from '@/lib/telegram';

/**
 * Минималистичный textarea в духе Notion/Linear:
 *  • прозрачный фон, мягкая рамка снизу, без жёсткого rounded-box
 *  • счётчик символов справа
 *  • кнопка «развернуть» в углу — открывает full-screen оверлей с этим же value
 *  • Esc / тап «Готово» возвращает изменения и закрывает overlay
 *
 * Использование:
 *   <BigTextarea label="Бриф" placeholder="..." value={x} onChange={setX} rows={6} />
 */
export interface BigTextareaProps {
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  maxLength?: number;
  hint?: string;
}

export function BigTextarea({
  label, placeholder, value, onChange, rows = 5, maxLength, hint,
}: BigTextareaProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-baseline justify-between">
          <label className="text-xs font-semibold uppercase tracking-wider text-ink-300">{label}</label>
          {hint && <span className="text-2xs text-ink-500">{hint}</span>}
        </div>
      )}

      <div className="group relative">
        <textarea
          className="ta-minimal"
          placeholder={placeholder}
          rows={rows}
          value={value}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
        {/* expand-кнопка в правом верхнем углу */}
        <button
          type="button"
          onClick={() => { haptic('light'); setOpen(true); }}
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-black/50 text-ink-300 opacity-0 backdrop-blur transition group-hover:opacity-100 group-focus-within:opacity-100 active:scale-95"
          aria-label="развернуть"
          title="Развернуть на весь экран"
        >
          <Icon name="plus" size={14} />
        </button>
        {/* счётчик */}
        <div className="mt-1 flex items-center justify-between text-2xs text-ink-500">
          <span>{value.length} симв.</span>
          {maxLength && <span>{value.length}/{maxLength}</span>}
        </div>
      </div>

      {open && (
        <FullscreenEditor
          value={value}
          placeholder={placeholder}
          label={label}
          onClose={() => setOpen(false)}
          onChange={onChange}
        />
      )}
    </div>
  );
}

function FullscreenEditor({
  value, placeholder, label, onClose, onChange,
}: {
  value: string; placeholder?: string; label?: string;
  onClose: () => void; onChange: (v: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const t = setTimeout(() => ref.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onChange(draft); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [draft, onChange, onClose]);

  const done = () => { haptic('success'); onChange(draft); onClose(); };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black/95 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3">
        <button
          onClick={onClose}
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-ink-100 active:scale-95"
          aria-label="закрыть"
        >
          <Icon name="chevron-left" size={20} />
        </button>
        <div className="min-w-0 flex-1 text-center">
          <div className="text-2xs uppercase tracking-widest text-ink-500">{label || 'редактирование'}</div>
          <div className="text-xs text-ink-300">{draft.length} симв.</div>
        </div>
        <button
          onClick={done}
          className="rounded-pill bg-accent-500 px-4 py-2 text-sm font-semibold text-white active:scale-95"
        >
          Готово
        </button>
      </div>
      <div className="flex-1 overflow-hidden px-4 pb-[max(20px,env(safe-area-inset-bottom))]">
        <textarea
          ref={ref}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          className="h-full w-full resize-none bg-transparent text-base leading-relaxed text-ink-50 placeholder:text-ink-500 outline-none"
        />
      </div>
    </div>
  );
}
